import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { toast } from 'react-hot-toast';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';

const filterOptions = createFilterOptions({
  stringify: (option) =>
    `${option.name ?? ''} ${option.email ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

/**
 * Dialog: pick one or more ACTIVE tenant users and add them to the current group.
 * @param {(userIds: string[]) => Promise<boolean>} onAssignUsers — return true to close dialog after success
 */
const AssignUsersIntoGroup = ({
  open,
  onClose,
  groupId,
  registryTenantId,
  memberUserIds = [],
  onAssignUsers,
}) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const memberSet = useMemo(
    () => new Set((memberUserIds ?? []).map((id) => String(id))),
    [memberUserIds]
  );

  const selectableUsers = useMemo(
    () => users.filter((u) => !memberSet.has(String(u.id))),
    [users, memberSet]
  );

  const loadUsers = useCallback(async () => {
    if (!registryTenantId || !open) return;
    setLoadingUsers(true);
    try {
      const rows = await fetchActiveTenantUsers(registryTenantId, { page: 0, size: 500 });
      setUsers(rows);
    } catch (error) {
      const msg =
        error.response?.data?.message || error.message || 'Failed to load tenant users';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load users');
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [registryTenantId, open]);

  useEffect(() => {
    if (open) {
      setSelectedUsers([]);
      loadUsers();
    }
  }, [open, loadUsers]);

  const handleAssign = async () => {
    const ids = selectedUsers.map((u) => String(u.id));
    if (!ids.length || !groupId) {
      toast.error('Select at least one user');
      return;
    }
    setSubmitting(true);
    try {
      const shouldClose = await onAssignUsers(ids);
      if (shouldClose) onClose();
    } catch (error) {
      const msg =
        error.response?.data?.message || error.message || 'Failed to assign users';
      toast.error(typeof msg === 'string' ? msg : 'Failed to assign');
    } finally {
      setSubmitting(false);
    }
  };

  const noTenant = !registryTenantId || String(registryTenantId).trim() === '';

  const getOptionLabel = (u) => {
    const name = u.name?.trim();
    const email = u.email?.trim();
    if (name && email) return `${name} — ${email}`;
    return name || email || String(u.id);
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Assign users into group</DialogTitle>
      <DialogContent>
        {noTenant ? (
          <Typography variant="body2" color="error" sx={{ pt: 1 }}>
            No tenant id in session. Log in with a tenant user or set REACT_APP_TENANT_ID.
          </Typography>
        ) : loadingUsers ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 2 }}
          >
            <CircularProgress size={20} />
            Loading active users…
          </Typography>
        ) : selectableUsers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            {users.length === 0
              ? 'No ACTIVE users found for this tenant.'
              : 'All active users are already in this group.'}
          </Typography>
        ) : (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={selectableUsers}
            value={selectedUsers}
            onChange={(_, newValue) => setSelectedUsers(newValue)}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
            filterOptions={filterOptions}
            loading={loadingUsers}
            sx={{ mt: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Users"
                placeholder="Search by name, email, or id…"
                helperText={`${selectedUsers.length} selected — type to filter the list`}
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={
            submitting || noTenant || selectedUsers.length === 0 || loadingUsers
          }
          sx={{ textTransform: 'none' }}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignUsersIntoGroup;
